import { type Device, type DeviceHome, DeviceStatus } from '@lmc/ts-common'
import { Button, Text } from '@lmc/ui-kit/src/general'
import Table from '@lmc/ui-kit/src/legacy/molecules/Table/Table'
import { showToast } from 'common/utils'
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog'
import { NAMESPACE } from 'locale'
import { dacConstants } from 'locale/deviceAccessControl'
import type { Dispatch, FC, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { getBadgeType } from '../../utils/devices'
import { cssDeviceTable } from './deviceTableStyles'

type DevicesManageTableProps = {
  device: Device
  updateDevice: ({
    homes,
    id
  }: {
    id?: string
    homes: { home: string; status: DeviceStatus }[]
  }) => Promise<unknown>
  id?: string
  isLoading?: boolean
  isLoadingUpdateDevice?: boolean
  isInlineEditing?: boolean
  selectedHomes: string[]
  setSelectedHomes: Dispatch<SetStateAction<string[]>>
  hasHomesList?: boolean
  homeId?: string
  testId?: string
}

const DevicesManageTable: FC<DevicesManageTableProps> = ({
  device,
  updateDevice,
  id,
  isLoading,
  isLoadingUpdateDevice,
  isInlineEditing,
  selectedHomes,
  setSelectedHomes,
  hasHomesList,
  homeId,
  testId
}) => {
  const { t } = useTranslation(NAMESPACE.DEVICE_ACCESS_CONTROL)

  const getRows = () =>
    device?.homes?.map((home: DeviceHome) => {
      const isCurrentSite = home.home.id === homeId

      return {
        name: { name: home.home.name, isCurrentSite },
        status: home.status,
        action: { home: home.home.id, status: home.status },
        key: home.home.id
      }
    })

  const handleChangeStatus = (status: DeviceStatus, home: string) =>
    updateDevice({
      id,
      homes: [{ status, home }]
    })
      .then(() =>
        showToast({
          type: 'success',
          title: t(
            status === DeviceStatus.approved
              ? dacConstants.ACCESS_APPROVED
              : dacConstants.ACCESS_BLOCKED
          )
        })
      )
      .catch(() =>
        showToast({ type: 'error', title: t(dacConstants.UPDATE_FAILED) })
      )

  const columns = [
    {
      dataIndex: 'name',
      title: t(dacConstants.SITE_NAME),
      render: ({
        name,
        isCurrentSite
      }: {
        name: string
        isCurrentSite: boolean
      }) => (
        <StyledNameWrapper>
          <Text>{name}</Text>
          {isCurrentSite && (
            <StyledCurrentSiteWrapper>
              {t(dacConstants.CURRENT_SITE)}
            </StyledCurrentSiteWrapper>
          )}
        </StyledNameWrapper>
      )
    },
    {
      dataIndex: 'status',
      width: '25%',
      title: t(dacConstants.STATUS),
      render: (status: DeviceStatus) => getBadgeType(status, t)
    },
    {
      dataIndex: 'action',
      width: '30%',
      title: t(dacConstants.ACTION),
      render: ({ home, status }: { home: string; status: DeviceStatus }) => (
        <ActionButtonsWrapper>
          {(status === DeviceStatus.approved ||
            status === DeviceStatus.pending) && (
            <ConfirmationDialog
              title={t(dacConstants.BLOCKING_ACCESS)}
              confirmText={t(dacConstants.BLOCK_ACCESS)}
              confirmVariant="danger"
              description={
                <>
                  <Text level={1}>{t(dacConstants.ABOUT_TO_BLOCK_ACCESS)}</Text>
                  <StyledTextDesc level={1}>
                    {t(dacConstants.ARE_YOU_SURE_CONTINUE)}
                  </StyledTextDesc>
                </>
              }
            >
              <Button
                disabled={isInlineEditing}
                variant="default"
                onClick={() => handleChangeStatus(DeviceStatus.blocked, home)}
                data-cy="btn-block"
              >
                {t(dacConstants.BLOCK)}
              </Button>
            </ConfirmationDialog>
          )}
          {(status === DeviceStatus.blocked ||
            status === DeviceStatus.pending) && (
            <ConfirmationDialog
              title={t(dacConstants.APPROVING_ACCESS)}
              confirmText={t(dacConstants.APPROVE_ACCESS)}
              confirmVariant="primary"
              description={
                <>
                  <Text level={1}>
                    {t(dacConstants.ABOUT_TO_APPROVE_ACCESS)}
                  </Text>
                  <StyledTextDesc level={1}>
                    {t(dacConstants.ARE_YOU_SURE_CONTINUE)}
                  </StyledTextDesc>
                </>
              }
            >
              <Button
                disabled={isInlineEditing}
                variant="default"
                onClick={() => handleChangeStatus(DeviceStatus.approved, home)}
                data-cy="btn-approve"
              >
                {t(dacConstants.APPROVE)}
              </Button>
            </ConfirmationDialog>
          )}
        </ActionButtonsWrapper>
      )
    }
  ]

  return (
    <TableStyled
      tableLayout="fixed"
      loading={isLoading || isLoadingUpdateDevice}
      rowSelection={
        hasHomesList
          ? {
              type: 'checkbox',
              onChange: (selectedRowKeys: React.Key[]) => {
                setSelectedHomes(selectedRowKeys as string[])
              },
              selectedRowKeys: selectedHomes,
              getCheckboxProps: () => ({
                disabled: isInlineEditing
              })
            }
          : undefined
      }
      pagination={false}
      columns={columns}
      dataSource={getRows()}
      data-testid={testId}
    />
  )
}

const ActionButtonsWrapper = styled.div`
  display: flex;
  gap: 8px;
`

const StyledTextDesc = styled(Text)`
  font-weight: 500;
`

const StyledCurrentSiteWrapper = styled(Text)`
  margin-left: 5px;
  color: ${(props) => props.theme.color.neutral.color5};
`

const StyledNameWrapper = styled.div`
  display: flex;
`

const TableStyled = styled(Table)`
  ${cssDeviceTable}
` as typeof Table

export default DevicesManageTable
